
<template>
    <StudentDashboardPageLayout id="tour-student-courses-page">

            <!-- Calendar Selector — current calendar first, then past registrations -->
            <div class="flex items-center gap-3">
                <label class="text-sm font-medium text-gray-700">View Registration:</label>
                <Select
                    v-model="selectedCalendarId"
                    :options="calendarOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Current Semester"
                    class="w-64"
                    showClear
                    data-testid="select-registration-calendar"
                />
            </div>

            <!-- Past Registration View (read-only) -->
            <div v-if="!isViewingCurrentCalendar && pastRegistration" class="bg-white rounded-lg border border-gray-100 p-4">
                <div class="flex items-center gap-2 mb-4">
                    <i class="pi pi-history text-primary"></i>
                    <h3 class="text-lg font-bold text-gray-800">{{ pastRegistrationLabel }}</h3>
                    <Tag :value="formatStatus(pastRegistration.approval_status ?? '')"
                        :severity="getStatusSeverity(pastRegistration.approval_status)" />
                </div>
                <RegisteredCoursesTable :courses="pastRegistration.coursesRegistered || []" />
            </div>

            <!-- Loading past registration -->
            <div v-else-if="!isViewingCurrentCalendar && loadingPast" class="flex justify-center items-center h-32">
                <i class="pi pi-spin pi-spinner text-2xl text-primary"></i>
            </div>

            <!-- Selected a past calendar the student has no registration for -->
            <div v-else-if="!isViewingCurrentCalendar"
                class="bg-white rounded-lg border border-gray-100 p-6 text-center text-gray-500"
                data-testid="past-registration-empty">
                <i class="pi pi-inbox text-2xl mb-2 block"></i>
                <p>No course registration found for {{ pastRegistrationLabel }}.</p>
            </div>

            <!-- Current Semester Registration (existing content) -->
            <div v-else>

                    <!-- Loading State -->
                <div v-if="loading" class="flex justify-center items-center h-64">
                    <i class="pi pi-spin pi-spinner text-4xl text-primary"></i>
                </div>

                <!-- Error State -->
                <div v-else-if="error" class="bg-red-50 text-red-600 p-4 rounded-lg flex items-center gap-2">
                    <i class="pi pi-exclamation-circle text-xl"></i>
                    <span>{{ error }}</span>
                    <Button label="Retry" class="p-button-sm p-button-text ml-auto" @click="fetchData" data-testid="btn-retry-fetch-registration" />
                </div>

                <div v-else>
                    <!-- Master switch: the school has closed course registration -->
                    <div v-if="isRegistrationClosed"
                        class="card bg-white rounded-lg border border-gray-100 p-8 mt-10 text-center"
                        data-testid="registration-closed-panel">
                        <span class="inline-flex h-12 w-12 items-center justify-center rounded-full bg-orange-50 mb-3">
                            <i class="pi pi-lock text-xl text-orange-500"></i>
                        </span>
                        <h2 class="text-lg font-bold text-gray-800 mb-1">Course registration is not open</h2>
                        <p class="text-sm text-gray-500 max-w-md mx-auto">
                            Your school has not opened course registration yet. Please check back later
                            or contact your school for more information.
                        </p>
                    </div>

                    <template v-else>
                    <!-- Financial clearance gate (per-school opt-in) -->
                    <Message v-if="isBlockedByFinancialClearance" severity="warn" :closable="false" class="mt-10"
                        data-testid="financial-clearance-banner">
                        <div class="flex flex-col gap-1">
                            <span class="font-semibold">Financial clearance required</span>
                            <span>
                                Course registration cannot be submitted. Please complete the required payment
                                or ensure your scholarship has been approved before proceeding.
                            </span>
                            <span v-if="unmetClearancePegs.length" class="flex flex-col gap-0.5" data-testid="clearance-peg-progress">
                                <span v-for="peg in unmetClearancePegs" :key="peg.bill_id">
                                    {{ peg.bill_label }}: paid <strong>{{ formatCurrency(peg.amount_counted) }}</strong>
                                    of {{ formatCurrency(peg.amount_required) }} required
                                    ({{ peg.percentage_achieved }}% of the {{ peg.percentage_required }}% threshold)
                                </span>
                            </span>
                            <span v-else-if="clearanceOutstandingBalance > 0" data-testid="clearance-outstanding-amount">
                                Outstanding balance: <strong>{{ formatCurrency(clearanceOutstandingBalance) }}</strong>
                            </span>
                            <Button label="Go to Billing" icon="pi pi-wallet" text severity="warn" size="small"
                                class="self-start -ml-2" @click="router.push({ name: 'student-billing' })"
                                data-testid="btn-clearance-go-billing" />
                        </div>
                    </Message>

                    <ModuleRegistrationContext
                        :delivery-mode="studentAcademicCalendar?.programme?.delivery_mode"
                        :module="openModules.find(module => module.id === selectedModuleId)"
                        :session-name="currentCalendar?.session?.name"
                        :semester-name="currentCalendar?.termStructure?.name"
                    />
                    <ModuleRegistrationPanel v-if="usesModules" v-model="selectedModuleId" v-model:module-ids="selectedModuleIds" :modules="openModules" :error="moduleError" />
                    <div v-if="usesModules && selectedModuleIds.length" class="space-y-2">
                        <h3 class="font-semibold">Selected modules</h3>
                        <div v-for="selection in moduleSelections" :key="selection.id" class="rounded border p-3 text-sm">
                            <Button :label="selection.name" text @click="selectedModuleId = selection.id" />
                            {{ selection.courses.length }} courses · {{ selection.units }} units
                            <p>Minimum: {{ selection.limits.min.value }} ({{ selection.limits.min.source }}) · Maximum: {{ selection.limits.max.value }} ({{ selection.limits.max.source }})</p>
                        </div>
                        <ModulePaymentPanel :module-ids="selectedModuleIds" :summary="modulePayments.summary.value"
                            :loading="modulePayments.loading.value" :error="modulePayments.error.value?.message"
                            @refresh="modulePayments.refetch()?.catch(() => undefined)" />
                    </div>
                    <!-- Registration Form -->
                    <div v-if="canRegister || isResubmitting"
                        class="card bg-white rounded-lg border border-gray-100 p-4 mt-10">

                        <!-- Instructions -->
                        <div class="mb-4 bg-primary/10 text-primary-700 p-3 rounded text-sm flex items-start gap-2">
                            <i class="pi pi-info-circle mt-0.5"></i>
                            <div>
                                <p class="font-semibold">Instructions:</p>
                                <ul class="list-disc list-inside mt-1">
                                    <li>Outstanding compulsory courses are <strong>locked</strong> and must be registered.</li>
                                    <li>Outstanding elective courses are optional — select if you wish to retake.</li>
                                    <li>Ensure the total number of units is between the minimum and maximum allowed.</li>
                                    <li>Click <strong>Submit</strong> if this is your first registration, or <strong>Resubmit</strong> to update.</li>
                                </ul>
                            </div>
                        </div>

                        <Message v-if="unitLimitConfigurationError" severity="error" :closable="false" class="mb-3">
                            {{ unitLimitConfigurationError }}
                        </Message>
                        <p class="text-xs text-gray-600 mb-2">{{ usesModules ? 'Limits for the selected module' : 'Limits for this semester' }}</p>
                        <!-- Registration Limits -->
                        <div v-if="studentAcademicCalendar" class="mb-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
                            <div class="bg-gray-50 border border-gray-300 rounded-lg p-3 text-center">
                                <p class="text-xs text-gray-500">Minimum Units</p>
                                <p class="text-lg font-semibold text-gray-800">{{ minUnitsAllowed }}</p>
                                <p class="text-xs text-gray-500">{{ effectiveUnitLimits.min.source }}</p>
                            </div>
                            <div class="bg-gray-50 border border-gray-300 rounded-lg p-3 text-center">
                                <p class="text-xs text-gray-500">Maximum Units</p>
                                <p class="text-lg font-semibold text-gray-800">{{ Number.isFinite(maxUnitsAllowed) ? maxUnitsAllowed : 'No maximum' }}</p>
                                <p class="text-xs text-gray-500">{{ effectiveUnitLimits.max.source }}</p>
                            </div>
                            <div class="rounded-lg p-3 text-center"
                                :class="totalUnits < minUnitsAllowed || totalUnits > maxUnitsAllowed ? 'bg-red-50 text-red-700' : 'bg-primary/10 text-primary'">
                                <p class="text-xs">Selected Units</p>
                                <p class="text-lg font-bold">{{ totalUnits }}</p>
                            </div>
                            <div v-if="hasOutstandingCourses" class="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                                <p class="text-xs text-orange-600">Outstanding Units</p>
                                <p class="text-lg font-bold text-orange-700">{{ outstandingTotalUnits }}</p>
                            </div>
                        </div>

                        <!-- ============ OUTSTANDING COURSES SECTION ============ -->
                        <div v-if="hasOutstandingCourses" class="mb-6">
                            <div class="flex items-center gap-2 mb-3">
                                <i class="pi pi-exclamation-triangle text-orange-500"></i>
                                <h3 class="text-sm font-bold text-orange-800 uppercase">Outstanding / Carryover Courses</h3>
                            </div>

                            <!-- Outstanding Compulsory -->
                            <div v-if="outstandingCompulsoryCourses.length > 0" class="mb-3">
                                <div class="bg-red-50 border border-red-200 rounded-lg overflow-hidden">
                                    <div class="px-3 py-2 bg-red-100 border-b border-red-200">
                                        <span class="text-xs font-bold text-red-800 uppercase">
                                            <i class="pi pi-lock text-xs mr-1"></i>
                                            Compulsory (must register) — {{ outstandingCompulsoryCourses.reduce((s, c) => s + c.unit, 0) }} units
                                        </span>
                                    </div>
                                    <DataTable :value="outstandingCompulsoryCourses"
                                        dataKey="id" size="small" stripedRows :rowClass="() => 'bg-red-50/30'">
                                        <Column headerStyle="width: 3rem">
                                            <template #body>
                                                <Checkbox :modelValue="true" :binary="true" disabled />
                                            </template>
                                        </Column>
                                        <Column field="course_code" header="Code" sortable class="font-medium">
                                            <template #body="{ data }">
                                                <div class="flex items-center gap-1.5">
                                                    <i class="pi pi-lock text-red-400 text-xs" />
                                                    <span>{{ data.course_code }}</span>
                                                </div>
                                            </template>
                                        </Column>
                                        <Column field="course_title" header="Title" sortable />
                                        <Column field="unit" header="Units" sortable style="width: 5rem">
                                            <template #body="{ data }">
                                                <span class="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-bold">{{ data.unit }}</span>
                                            </template>
                                        </Column>
                                        <Column header="Level" style="width: 6rem">
                                            <template #body="{ data }">
                                                <span class="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Level {{ data.originalLevel }}</span>
                                            </template>
                                        </Column>
                                    </DataTable>
                                </div>
                            </div>

                            <!-- Outstanding Elective -->
                            <div v-if="outstandingElectiveCourses.length > 0">
                                <div class="bg-yellow-50 border border-yellow-200 rounded-lg overflow-hidden">
                                    <div class="px-3 py-2 bg-yellow-100 border-b border-yellow-200">
                                        <span class="text-xs font-bold text-yellow-800 uppercase">
                                            Elective (optional retake) — {{ outstandingElectiveCourses.reduce((s, c) => s + c.unit, 0) }} units available
                                        </span>
                                    </div>
                                    <DataTable :value="outstandingElectiveCourses"
                                        dataKey="id" size="small" stripedRows>
                                        <Column headerStyle="width: 3rem">
                                            <template #body="{ data }">
                                                <Checkbox :modelValue="selectedCourses.some(c => c.id === data.id)"
                                                    :binary="true"
                                                    @update:modelValue="(val) => toggleElectiveOutstanding(data, val)" />
                                            </template>
                                        </Column>
                                        <Column field="course_code" header="Code" sortable class="font-medium" />
                                        <Column field="course_title" header="Title" sortable />
                                        <Column field="unit" header="Units" sortable style="width: 5rem">
                                            <template #body="{ data }">
                                                <span class="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded text-xs font-bold">{{ data.unit }}</span>
                                            </template>
                                        </Column>
                                        <Column header="Level" style="width: 6rem">
                                            <template #body="{ data }">
                                                <span class="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Level {{ data.originalLevel }}</span>
                                            </template>
                                        </Column>
                                    </DataTable>
                                </div>
                            </div>
                        </div>

                        <!-- ============ CURRENT LEVEL COURSES ============ -->
                        <div>
                            <div class="flex items-center gap-2 mb-3">
                                <i class="pi pi-book text-primary"></i>
                                <h3 class="text-sm font-bold text-gray-800 uppercase">Current Level Courses</h3>
                            </div>

                            <div class="flex justify-between items-center mb-2">
                                <span class="p-input-icon-left">
                                    <InputText v-model="filters.global.value" placeholder="Search by code or title..."
                                        inputClass="w-full" class="p-inputtext-md w-72" />
                                </span>
                            </div>

                            <DataTable :value="currentLevelCourses"
                                dataKey="id" :filters="filters" filterDisplay="menu"
                                :globalFilterFields="['course_code', 'course_title']"
                                class="p-datatable-md" :rowHover="true" stripedRows responsiveLayout="scroll">
                                <Column headerStyle="width: 3rem">
                                    <template #body="{ data }">
                                        <Checkbox :modelValue="selectedCourses.some(c => c.id === data.id)"
                                            :binary="true"
                                            @update:modelValue="(val) => toggleCurrentLevel(data, val)" />
                                    </template>
                                </Column>
                                <Column field="course_code" header="Course Code" sortable class="font-medium" />
                                <Column field="course_title" header="Course Title" sortable />
                                <Column field="unit" header="Units" sortable>
                                    <template #body="slotProps">
                                        <span class="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs font-bold">{{ slotProps.data.unit }}</span>
                                    </template>
                                </Column>
                                <Column header="Status">
                                    <template #body="{ data }">
                                        <span class="px-2 py-1 text-xs font-semibold rounded"
                                            :class="data.isCompulsory ? 'bg-primary/10 text-primary' : 'bg-orange-100 text-orange-700'">
                                            {{ data.isCompulsory ? 'Compulsory' : 'Elective' }}
                                        </span>
                                    </template>
                                </Column>
                                <template #footer>
                                    <div class="flex justify-between items-center px-2">
                                        <span class="text-sm text-gray-600">{{ currentLevelCourses.length }} course(s)</span>
                                        <span class="text-sm font-bold text-gray-800">
                                            {{ currentLevelCourses.reduce((s, c) => s + c.unit, 0) }} units
                                        </span>
                                    </div>
                                </template>
                            </DataTable>
                        </div>

                        <!-- ============ ADD EXTRA ELECTIVE ============ -->
                        <div class="mt-4" data-testid="extra-elective-search">
                            <div class="flex items-center gap-2 mb-2">
                                <i class="pi pi-search text-gray-500"></i>
                                <h3 class="text-sm font-bold text-gray-800 uppercase">Add More Elective Courses</h3>
                            </div>
                            <p v-if="usesModules" class="text-xs text-gray-500 mb-2">
                                Search eligible electives, including school-wide courses your school has offered in the selected open module. Added courses appear in your selection
                                and count toward this module’s unit limits.
                            </p>
                            <p v-else class="text-xs text-gray-500 mb-2">Search for additional elective courses from the curriculum for this semester.</p>
                            <div class="flex gap-2 items-end">
                                <div class="flex-1">
                                    <Select v-model="extraElectiveId" :options="extraCourseOptions" optionLabel="label" optionValue="value"
                                        placeholder="Search course by code or title..." filter @filter="onExtraCourseFilter" showClear class="w-full"
                                        :loading="usesModules && loadingModuleElectives"
                                        :emptyMessage="usesModules ? 'No additional eligible elective courses in this module.' : 'No courses available.'"
                                        emptyFilterMessage="No matching eligible courses." />
                                </div>
                                <Button label="Add" icon="pi pi-plus" size="small"
                                    :disabled="!extraElectiveId" @click="addExtraElective" />
                            </div>

                            <Message v-if="usesModules && moduleElectiveSearchError" severity="error" :closable="false" class="mt-2">
                                {{ moduleElectiveSearchError.message }}
                            </Message>
                            <!-- Selected Extra Electives -->
                            <div v-if="extraElectives.length > 0" class="mt-3 bg-green-50 border border-green-200 rounded-lg overflow-hidden">
                                <div class="px-3 py-2 bg-green-100 border-b border-green-200">
                                    <span class="text-xs font-bold text-green-800 uppercase">
                                        Selected Extra Electives — {{ extraElectives.reduce((s, c) => s + c.unit, 0) }} units
                                    </span>
                                </div>
                                <div class="divide-y divide-green-100">
                                    <div v-for="course in extraElectives" :key="course.id"
                                        class="flex items-center justify-between px-3 py-2">
                                        <div class="flex items-center gap-2">
                                            <span class="text-sm font-medium">{{ course.course_code }}</span>
                                            <span class="text-xs text-gray-500">{{ course.course_title }}</span>
                                            <span class="bg-green-200 text-green-800 px-1.5 py-0.5 rounded text-xs font-bold">{{ course.unit }}u</span>
                                        </div>
                                        <Button icon="pi pi-times" size="small" severity="danger" text rounded
                                            @click="removeExtraElective(course.id)" v-tooltip="'Remove'" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Submit Button -->
                        <div class="mt-6 flex justify-end gap-3 pt-4 border-t border-gray-100">
                            <Button
                                :label="hasPreviouslyRegistered ? 'Resubmit Registration' : 'Submit Registration'"
                                :icon="hasPreviouslyRegistered ? 'pi pi-refresh' : 'pi pi-check'"
                                @click="submitRegistration"
                                :loading="submitting"
                                :disabled="isBlockedByFinancialClearance || selectedCourses.length === 0 || totalUnits < minUnitsAllowed || totalUnits > maxUnitsAllowed"
                                data-testid="btn-submit-registration"
                            />
                        </div>

                    </div>

                    <!-- Registration Submitted View -->
                    <div v-else class="bg-white rounded-lg shadow-sm border border-gray-100 mt-10"
                        data-testid="registration-submitted-panel">
                        <!-- Summary Header -->
                        <div
                            class="flex flex-col gap-3 border-b border-gray-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                            <div class="flex min-w-0 items-center gap-3">
                                <span
                                    class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                                    <i class="pi pi-check-circle text-xl text-primary"></i>
                                </span>
                                <div class="min-w-0">
                                    <div class="flex flex-wrap items-center gap-2">
                                        <h2 class="text-lg font-bold leading-tight text-gray-800">Registered Courses</h2>
                                        <Tag
                                            v-if="registrationStatus"
                                            :value="registrationStatus.label"
                                            :severity="registrationStatus.severity"
                                        />
                                    </div>
                                    <p class="mt-0.5 text-sm text-gray-500">
                                        {{ registeredSemesterLabel || 'Current semester' }}
                                        &middot; {{ alreadyRegisteredCourses.length }} course{{ alreadyRegisteredCourses.length === 1 ? '' : 's' }}
                                        &middot; {{ registeredTotalUnits }} unit{{ registeredTotalUnits === 1 ? '' : 's' }}
                                    </p>
                                </div>
                            </div>

                            <Button label="Resubmit Courses" icon="pi pi-refresh" severity="secondary" outlined
                                size="small" class="shrink-0 self-start sm:self-auto"
                                :disabled="isBlockedByFinancialClearance"
                                @click="enableResubmit" data-testid="btn-enable-resubmit" />
                        </div>

                        <!-- Registered courses, inline (no modal) -->
                        <RegisteredCoursesTable :courses="alreadyRegisteredCourses" />
                    </div>
                    </template>
                </div>

            </div> <!-- end v-else (viewing the current calendar) -->

    </StudentDashboardPageLayout>
</template>

<script setup lang="ts">
import { gql } from '@apollo/client/core';
import ModuleRegistrationContext from './ModuleRegistrationContext.vue';
import { useRouter } from 'vue-router';
import { useApolloClient } from '@vue/apollo-composable';
import { useUserQuery } from '@/composables/queries/useUserQuery';
import ModulePaymentPanel from './ModulePaymentPanel.vue';
import { useCourseRegistration } from '@/composables/useCourseRegistration';
import { getStatusSeverity } from '@/utils/statusSeverity';
import {
  buildCalendarViewOptions,
  formatCalendarLabel,
  type CalendarViewOption,
} from '@/utils/registrationCalendarOptions';
import { formatCurrency } from '@/utils/currency';

const {
  studentAcademicCalendar,
  filters,
  minUnitsAllowed,
  maxUnitsAllowed,
  loading,
  submitting,
  error,
  canRegister,
  isResubmitting,
  alreadyRegisteredCourses,
  selectedCourses,
  totalUnits,
  registeredTotalUnits,
  registeredSemesterLabel,
  hasPreviouslyRegistered,
  compulsoryOutstandingUnits,
  currentLevelCompulsoryOverflow,
  outstandingCompulsoryCourses,
  outstandingElectiveCourses,
  currentLevelCourses,
  hasOutstandingCourses,
  outstandingTotalUnits,
  submitRegistration,
  enableResubmit,
  fetchData,
  toggleElectiveOutstanding,
  toggleCurrentLevel,
  extraElectiveId,
  extraCourseOptions,
  onExtraCourseFilter,
  addExtraElective,
  extraElectives,
  removeExtraElective,
  isBlockedByFinancialClearance,
  clearanceOutstandingBalance,
  unmetClearancePegs,
  isRegistrationClosed,
  loadingModuleElectives, moduleElectiveSearchError,
  selectedModuleIds, moduleSelections, modulePayments,
  usesModules, openModules, selectedModuleId, moduleError, effectiveUnitLimits, unitLimitConfigurationError,
} = useCourseRegistration();

const router = useRouter();

// Approval state of the registration the student already submitted this semester.
const { studentProfile, studentregcurrentsemester, academicCalendars } = useUserQuery();

const formatStatus = (status: string) =>
  status.toLowerCase() === 'hod_endorsed'
    ? 'HOD Endorsed'
    : status.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());

const registrationStatus = computed(() => {
  const status = studentregcurrentsemester.value?.approval_status;
  if (!status) return null;
  return { label: formatStatus(String(status)), severity: getStatusSeverity(String(status)) };
});

// --- Past Registration Viewer ---
const { resolveClient } = useApolloClient();

interface PastRegistrationCalendar {
  id: string | number;
  session?: { name?: string | null } | null;
  termStructure?: { name?: string | null } | null;
}

interface PastRegisteredCourse {
  course_status?: string | null;
  course?: { course_code?: string | null; course_title?: string | null; unit?: number | null } | null;
}

interface PastRegistrationRecord {
  academic_calendar_id?: string | number | null;
  approval_status?: string | null;
  coursesRegistered?: PastRegisteredCourse[] | null;
}

const selectedCalendarId = ref<string>('');
const pastRegistration = ref<PastRegistrationRecord | null>(null);
const loadingPast = ref(false);
const registeredCalendars = ref<PastRegistrationCalendar[]>([]);

// Fetch calendars the student has course registrations for
watch(() => studentProfile.value?.id, async(id) => {
  if (!id) return;
  try {
    const client = resolveClient();
    const { data } = await client.query({
      query: gql`
        query GetStudentRegisteredCalendars($student_id: ID!) {
          studentRegisteredCalendars(student_id: $student_id) {
            id
            session { id name }
            termStructure { id name }
          }
        }
      `,
      variables: { student_id: String(id) },
      fetchPolicy: 'network-only',
    });
    registeredCalendars.value = data?.studentRegisteredCalendars || [];
  } catch {
    registeredCalendars.value = [];
  }
}, { immediate: true });

// The school's live calendar, which `studentRegisteredCalendars` only returns
// once the student has actually registered for it.
const currentCalendar = computed(() => academicCalendars.value.find(c => c?.is_active) ?? null);

const currentCalendarId = computed(() => {
  const id = currentCalendar.value?.id;

  return id ? String(id) : '';
});

const currentCalendarOption = computed<CalendarViewOption | null>(() =>
  currentCalendarId.value
    ? {
      label: formatCalendarLabel(currentCalendar.value?.session?.name, currentCalendar.value?.termStructure?.name),
      value: currentCalendarId.value,
    }
    : null,
);

const calendarOptions = computed(() =>
  buildCalendarViewOptions(
    currentCalendarOption.value,
    registeredCalendars.value.flatMap((c) => {
      const label = formatCalendarLabel(c.session?.name, c.termStructure?.name);
      if (!label) return [];

      return [{ label, value: String(c.id) }];
    }),
  ),
);

// Preselect the current calendar as soon as the me-query resolves, so the
// selector names the semester being registered for instead of sitting empty.
watch(currentCalendarId, (id) => {
  if (id && !selectedCalendarId.value) selectedCalendarId.value = id;
}, { immediate: true });

// An empty selection still means "current" — the placeholder state after the
// clear button, and the fallback when no active calendar could be resolved.
const isViewingCurrentCalendar = computed(() =>
  !selectedCalendarId.value || selectedCalendarId.value === currentCalendarId.value,
);

const pastRegistrationLabel = computed(() => {
  if (!selectedCalendarId.value) return '';
  const opt = calendarOptions.value.find((o: CalendarViewOption) => o.value === selectedCalendarId.value);
  return opt?.label || '';
});

const GET_PAST_REGISTRATION = gql`
  query GetPastRegistration($student_id: Int!) {
    getCourseRegistrationsForStudent(student_id: $student_id) {
      id
      academic_calendar_id
      approval_status
      registration_level
      coursesRegistered {
        id
        course_id
        course_status
        course {
          id
          course_code
          course_title
          unit
        }
      }
    }
  }
`;

watch(selectedCalendarId, async(calId) => {
  pastRegistration.value = null;
  if (!calId || isViewingCurrentCalendar.value || !studentProfile.value?.id) return;

  loadingPast.value = true;
  try {
    const client = resolveClient();
    const { data } = await client.query({
      query: GET_PAST_REGISTRATION,
      variables: { student_id: Number(studentProfile.value.id) },
      fetchPolicy: 'network-only',
    });

    const regs = data?.getCourseRegistrationsForStudent || [];
    pastRegistration.value = (regs as PastRegistrationRecord[])
      .find((r) => String(r.academic_calendar_id) === calId) || null;
  } catch {
    pastRegistration.value = null;
  } finally {
    loadingPast.value = false;
  }
});
</script>
